import {
  functionOf,
  ECall,
  EFunction,
  Expression,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {withSystem, withMeta, EnvTypes, Plugins} from '@packages/systems/wfdl';
import {_$, $} from '@packages/utilities/wfdl-helpers';
import {
  predicate,
  runPredicate,
  TPredicate,
  toBooleanTag,
  TBoolean,
  BooleanTag,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import {TCaseSensitiveText} from '@packages/systems/plugins/Text';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {LocalesDisplayNameType, TLocalesActive} from './types';

const LocalesDisplayImageIdType = TTypeConstructor(
  qualify(Plugins.name('Localization'), typeName('DisplayImageId'))
);

function createDisplayNamePredicates(): Record<string, EnvTypes.TypedValue> {
  const predicateType = functionOf(
    TCaseSensitiveText,
    TPredicate(LocalesDisplayNameType)
  );

  const createCaseSensitiveExpression = (
    textPredicateName: string
  ): Expression => {
    return EFunction({
      param: 'x',
      body: predicate(
        EFunction({
          param: 'localeValue',
          body: ECall({
            fun: runPredicate(
              ECall({
                fun: _$('Text', textPredicateName),
                arg: $('x'),
              })
            ),
            arg: ECall({
              fun: _$('Localization', 'displayNameToText'),
              arg: $('localeValue'),
            }),
          }),
        })
      ),
    });
  };

  const createSetExpression = (textPredicateName: string): Expression => {
    return predicate(
      EFunction({
        param: 'localeValue',
        body: ECall({
          fun: runPredicate(_$('Text', textPredicateName)),
          arg: ECall({
            fun: _$('Localization', 'displayNameToText'),
            arg: $('localeValue'),
          }),
        }),
      })
    );
  };

  return {
    DisplayNameIsSet: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is set'},
          TPredicate(LocalesDisplayNameType)
        ),
      },
      expression: createSetExpression('Is set'),
    },
    DisplayNameIsNotSet: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is not set'},
          TPredicate(LocalesDisplayNameType)
        ),
      },
      expression: createSetExpression('Is not set'),
    },
    DisplayNameCaseEquals: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseEquals'),
    },
    DisplayNameCaseDoesNotEqual: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is not'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseDoesNotEqual'),
    },
    DisplayNameCaseContains: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'contains'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseContains'),
    },
    DisplayNameCaseDoesNotContain: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'does not contain'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseDoesNotContain'),
    },
    DisplayNameCaseStartsWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'starts with'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseStartsWith'),
    },
    DisplayNameCaseDoesNotStartWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'does not start with'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseDoesNotStartWith'),
    },
    DisplayNameCaseEndsWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'ends with'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseEndsWith'),
    },
    DisplayNameCaseDoesNotEndWith: {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'does not end with'}, predicateType),
      },
      expression: createCaseSensitiveExpression('CaseDoesNotEndWith'),
    },
  };
}

export const displayNamePredicates = createDisplayNamePredicates();

function createDisplayImageIdPredicates(): Record<string, EnvTypes.TypedValue> {
  return {
    DisplayImageIdIsSet: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is set'},
          TPredicate(LocalesDisplayImageIdType)
        ),
      },
      value: (image: unknown) => {
        return toBooleanTag(image != null);
      },
    },
    DisplayImageIdIsNotSet: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is not set'},
          TPredicate(LocalesDisplayImageIdType)
        ),
      },
      value: (image: unknown) => {
        return toBooleanTag(image == null);
      },
    },
  };
}

export const displayImageIdPredicates = createDisplayImageIdPredicates();

const TOnOffBoolean = withMeta({trueLabel: 'On', falseLabel: 'Off'}, TBoolean);

function createActivePredicates(): Record<string, EnvTypes.TypedValue> {
  return {
    ActiveEquals: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is'},
          functionOf(TOnOffBoolean, TPredicate(TLocalesActive))
        ),
      },
      value: (conditionValue: BooleanTag) => (fieldValue: boolean) => {
        return toBooleanTag(fieldValue === (conditionValue.tag === TRUE.tag));
      },
    },
    ActiveDoesNotEqual: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is not'},
          functionOf(TOnOffBoolean, TPredicate(TLocalesActive))
        ),
      },
      value: (conditionValue: BooleanTag) => (fieldValue: boolean) => {
        return toBooleanTag(fieldValue !== (conditionValue.tag === TRUE.tag));
      },
    },
  };
}

export const activePredicates = createActivePredicates();
